import React from 'react';
import { Phone, ExternalLink, CalendarDays, CalendarClock, StickyNote, Tag } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  fullName, initials, formatBusinessDate, socialPlatformLabel, socialProfileUrl, todayISODate,
  businessWhatsAppUrl, openBusinessWhatsApp, teamStatusLabel, teamStatusBadgeClass,
} from '../../lib/businessHelpers';

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="text-xs text-zinc-400">{label}</p>
      <div className="text-sm text-zinc-800">{children}</div>
    </div>
  </div>
);

const TeamMemberDetailDialog = ({ open, onOpenChange, member }) => {
  if (!member) return null;
  const profileUrl = socialProfileUrl(member.social_platform, member.social_handle);
  const reminders = member.reminders || [];
  const today = todayISODate();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-zinc-200 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center font-semibold text-lg flex-shrink-0">
              {initials(member)}
            </div>
            <DialogTitle className="text-left">{fullName(member)}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {member.telefono && (
            <Row icon={Phone} label="Telefono">
              <a
                href={businessWhatsAppUrl(member.telefono)}
                onClick={(e) => { e.preventDefault(); openBusinessWhatsApp(member.telefono); }}
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-700 hover:underline"
              >
                {member.telefono}
              </a>
            </Row>
          )}

          {member.social_platform && (
            <Row icon={ExternalLink} label="Profilo social">
              {profileUrl ? (
                <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="text-orange-700 hover:underline inline-flex items-center gap-1">
                  {socialPlatformLabel(member.social_platform)} {member.social_handle}
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <span>{socialPlatformLabel(member.social_platform)} {member.social_handle}</span>
              )}
            </Row>
          )}

          {teamStatusLabel(member.status) && (
            <Row icon={Tag} label="Status">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${teamStatusBadgeClass(member.status)}`}>
                {teamStatusLabel(member.status)}
              </span>
            </Row>
          )}

          {member.data_iscrizione && (
            <Row icon={CalendarDays} label="Data iscrizione">
              {formatBusinessDate(member.data_iscrizione)}
            </Row>
          )}

          {reminders.length > 0 && (
            <Row icon={CalendarClock} label="Promemoria">
              <ul className="space-y-0.5">
                {reminders.map((r) => (
                  <li key={r.date} className={r.date <= today ? 'text-orange-700 font-medium' : ''}>
                    {formatBusinessDate(r.date)}{r.date <= today ? ' · da ricontattare' : ''}
                    {r.subject ? <span> · {r.subject}</span> : null}
                  </li>
                ))}
              </ul>
            </Row>
          )}

          {member.note && (
            <Row icon={StickyNote} label="Note">
              <p className="whitespace-pre-wrap">{member.note}</p>
            </Row>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TeamMemberDetailDialog;
